<script setup lang="ts">
  import { Skeleton, Typo } from '@travelata-libs/ui-kit';
  import { computed, nextTick, ref, watch } from 'vue';
  import { dataLayerTracker } from '@/entities/analytics';
  import type { SearchCriteria } from '@/entities/searchCriteria';
  import type { DateRange } from '@/shared/model';
  import LowPriceCalendarSkeleton from './LowPriceCalendarSkeleton.vue';
  import { toLowPriceCalendarDays } from '../lib/lowPriceDays';
  import { withProductPrices } from '../lib/productPrices';
  import type { LowPriceCalendarDay, LowPriceCalendarProduct } from '../model/types';
  import type { UseLowPriceCalendar } from '../model/useLowPriceCalendar';

  interface LowPriceCalendarProps {
    criteria: SearchCriteria;
    calendar: UseLowPriceCalendar;
    loading?: boolean;
    products?: LowPriceCalendarProduct[];
  }

  const props = withDefaults(defineProps<LowPriceCalendarProps>(), {
    loading: false,
    products: () => [],
  });

  interface LowPriceCalendarEmits {
    (e: 'select', dateRange: DateRange): void;
  }

  const emit = defineEmits<LowPriceCalendarEmits>();

  const LOAD_MORE_THRESHOLD_PX = 120;

  const isAvailable = computed<boolean>(() => props.calendar.isAvailable.value);
  const isLoadingMore = computed<boolean>(() => props.calendar.isLoadingMore.value);

  const isSkeletonVisible = computed<boolean>(
    () => isAvailable.value && (props.loading || props.calendar.isLoading.value),
  );

  const rootRef = ref<HTMLElement | null>(null);

  let scrolledToDate: string | null = null;
  let lastScrollLeft = 0;
  let isPrepending = false;

  const rememberScrollLeft = (): void => {
    lastScrollLeft = rootRef.value?.scrollLeft ?? 0;
  };

  const selectedDate = computed<string | null>(() => {
    const { dateRange } = props.criteria;

    return dateRange && dateRange.from === dateRange.to ? dateRange.from : null;
  });

  const calendarDays = computed<LowPriceCalendarDay[]>(() =>
    toLowPriceCalendarDays(
      withProductPrices(props.calendar.days.value, props.products, selectedDate.value),
      selectedDate.value,
    ),
  );

  const scrollToSelected = (): void => {
    const selected = rootRef.value?.querySelector<HTMLElement>('.low-price-calendar__day_selected');

    if (!rootRef.value || !selected) return;

    scrolledToDate = selectedDate.value;
    rootRef.value.scrollLeft = selected.offsetLeft - (rootRef.value.clientWidth - selected.offsetWidth) / 2;
    rememberScrollLeft();
  };

  watch(calendarDays, () => {
    if (!calendarDays.value.length || scrolledToDate === selectedDate.value) return;

    nextTick(scrollToSelected);
  });

  watch(isSkeletonVisible, (visible) => {
    if (visible) {
      scrolledToDate = null;
      lastScrollLeft = 0;
      return;
    }

    nextTick(scrollToSelected);
  });

  const prependPreviousDays = async (): Promise<void> => {
    const anchor = rootRef.value?.querySelector<HTMLElement>('.low-price-calendar__day');

    if (!anchor || isPrepending) return;

    isPrepending = true;

    const anchorOffset = anchor.offsetLeft;

    try {
      await props.calendar.loadPrevious();
      await nextTick();

      if (!rootRef.value?.contains(anchor)) return;

      rootRef.value.scrollLeft += anchor.offsetLeft - anchorOffset;
      rememberScrollLeft();
    } finally {
      isPrepending = false;
    }
  };

  const onScroll = (): void => {
    if (!rootRef.value) return;

    const { scrollLeft, clientWidth, scrollWidth } = rootRef.value;
    const movedLeft = scrollLeft < lastScrollLeft;

    lastScrollLeft = scrollLeft;

    if (scrollLeft + clientWidth >= scrollWidth - LOAD_MORE_THRESHOLD_PX) {
      props.calendar.loadMore();
      return;
    }

    if (movedLeft && scrollLeft <= LOAD_MORE_THRESHOLD_PX) prependPreviousDays();
  };

  const onSelect = (day: LowPriceCalendarDay): void => {
    dataLayerTracker.calendarDayClick(day.price !== null);

    if (day.selected) return;

    emit('select', { from: day.date, to: day.date, daysFlexible: 0 });
  };
</script>

<template>
  <LowPriceCalendarSkeleton v-if="isSkeletonVisible" />

  <div
    v-else-if="isAvailable && calendarDays.length"
    ref="rootRef"
    class="low-price-calendar"
    data-testid="low-price-calendar"
    @scroll="onScroll"
  >
    <button
      v-for="day in calendarDays"
      :key="day.date"
      type="button"
      :class="['low-price-calendar__day', { 'low-price-calendar__day_selected': day.selected }]"
      :aria-pressed="day.selected"
      :data-testid="`low-price-calendar-day-${day.date}`"
      @click="onSelect(day)"
    >
      <Typo
        class="low-price-calendar__date"
        tag="span"
        variant="body-s-400"
      >
        {{ day.label }}
      </Typo>

      <Typo
        :class="[
          'low-price-calendar__price',
          {
            'low-price-calendar__price_cheapest': day.cheapest,
            'low-price-calendar__price_priciest': day.priciest,
          },
        ]"
        tag="span"
        variant="body-s-400"
      >
        {{ day.priceLabel }}
      </Typo>
    </button>

    <div
      v-if="isLoadingMore"
      class="low-price-calendar__more"
      aria-hidden="true"
      data-testid="low-price-calendar-more"
    >
      <Skeleton
        width="84px"
        height="44px"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .low-price-calendar {
    box-sizing: border-box;
    display: flex;
    gap: var(--spacing-xxs);
    overflow-anchor: none;
    overflow-x: auto;
    padding: var(--spacing-xs);
    padding-top: var(--spacing-l);
    scrollbar-width: none;
    width: 100%;

    &::-webkit-scrollbar {
      display: none;
    }

    &__day {
      align-items: stretch;
      background: var(--color-bg-primary);
      border: none;
      border-radius: var(--rounding-m);
      cursor: pointer;
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      gap: var(--spacing-xxxs);
      justify-content: center;
      min-height: 44px;
      min-width: 84px;
      padding: var(--spacing-xxs) var(--spacing-xs);
      text-align: center;

      &_selected {
        background: linear-gradient(
          238deg,
          var(--color-yellow-40) 0%,
          var(--color-orange-40) 13%,
          var(--color-orange-70) 100%
        );
      }
    }

    &__more {
      border-radius: var(--rounding-m);
      display: flex;
      flex: 0 0 auto;
      overflow: hidden;
    }

    &__date {
      color: var(--color-text-secondary);
      white-space: nowrap;
    }

    &__price {
      color: var(--color-text-primary);
      white-space: nowrap;

      &_cheapest {
        color: var(--color-success-500);
      }

      &_priciest {
        color: var(--color-error-500);
      }
    }

    &__day_selected &__date,
    &__day_selected &__price {
      color: var(--color-text-inverse);
    }
  }
</style>
